import { isBefore, subYears } from 'date-fns';
import bankValidator from 'nz-bank-account-validator/lib/NZ-Bank-Account-Validator';
import { z } from 'zod';

import { CHECKOUT_TABS, InstallationType, isNotShownProvider } from '@/lib/constants';
import { checkValidAddress } from '@/lib/utils';
import {
  checkDigitOnly,
  checkNameTiles,
  checkValidCharacters,
  splitPhoneNumber,
  validateBroadbandMobileNumber,
  validatePhoneNumber,
} from '@/lib/validations';

export enum PaymentOptions {
  BANK = 'Direct Debit',
  CREDIT_CARD = 'CreditCard',
}

const OptionalDate = z.preprocess(value => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  return value;
}, z.coerce.date().optional());

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isDateBeforeToday = (date: Date) => isBefore(startOfDay(date), startOfDay(new Date()));

const MINIMUM_DATE_OF_BIRTH_YEAR = 1900;
const DATE_OF_BIRTH_INVALID_MESSAGE = 'Please enter a valid date';

const Address = z
  .object({
    label: z.string(),
    id: z.string(),
    tui: z.string().or(z.number()),
  })
  .nullable()
  .refine(value => checkValidAddress(value), {
    message: 'Valid address is required',
  });
const BaseAccountFormObject = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'Minimum length 2 characters')
    .max(10, 'Maximum length 10 characters')
    .refine(value => checkValidCharacters(value), 'Invalid characters')
    .refine(value => !checkNameTiles(value), 'No name tiles allowed'),
  lastName: z
    .string()
    .min(1, 'Family name is required')
    .min(2, 'Minimum length 2 characters')
    .max(15, 'Maximum length 15 characters')
    .refine(value => checkValidCharacters(value), 'Invalid characters')
    .refine(value => !checkNameTiles(value), 'No name tiles allowed'),
  email: z
    .string()
    .min(1, 'Email is required')
    .max(80, 'Email must be 80 characters of less')
    .email('Valid email is required'),
  areaCode: z.string().optional(),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  dateOfBirth: z
    .preprocess(
      val => {
        if (typeof val === 'string') {
          const trimmed = val.trim();
          if (trimmed === '') return undefined;

          const date = new Date(trimmed);
          if (isNaN(date.getTime())) return undefined; // invalid date string
          return date; // ✅ return actual Date object
        }

        // If val is already a Date, just return it
        if (val instanceof Date) return val;

        return undefined;
      },
      z.date({ invalid_type_error: 'Date of birth is invalid' })
    )
    .refine(val => val !== undefined, {
      message: 'Date of birth is required',
    })
    .refine(date => date === undefined || date.getFullYear() >= MINIMUM_DATE_OF_BIRTH_YEAR, {
      message: DATE_OF_BIRTH_INVALID_MESSAGE,
    })
    .refine(date => isBefore(date!, subYears(new Date(), 18)), {
      message: 'You must be 18 years or older',
    }),
  abandonedCartAck: z.boolean().optional(),
  saveModelAck: z.boolean().optional(),

  serviceAddress: Address,
  tui: z.string().or(z.number()),
  residencyStatus: z.number(),
  // installationDate: either a valid date or null
  installationDate: z.coerce
    .date({ invalid_type_error: 'Installation date is invalid' })
    .nullable()
    .refine(date => date === null || !isNaN(date.getTime()), {
      message: 'Installation date is required',
    }),

  // installationTime: either a non-empty string or null
  installationTime: z
    .string()
    .nullable()
    .refine(val => val === null || val.trim().length > 0, {
      message: 'Installation time is required',
    }),
  installationCode: z.string().nullable().optional(),

  isUsingExistingPhone: z.string().nonempty('Please select an option'),
  isKeepExistingPhoneNumber: z.string().optional(),
  isPhoneProviderMatchBroadband: z.string().optional(),
  currentProvider: z.string().optional(),
  currentProviderDropDown: z.string().optional(),
  otherProvider: z.string().optional(),
  currentServiceProvider: z.string().optional(),
  currentServiceDropDown: z.string().optional(),
  otherServiceProvider: z.string().optional(),
  providerAccountNumber: z.string().optional(),
  phoneNumberAreaCode: z.string().optional(),
  phoneNumberLineNumber: z.string().optional(),
  phoneNumberFromList: z.string().optional(),
  accountNumberForHomePhone: z.string().optional(),
  prefferedInstallationDate: OptionalDate,
  prefferedInstallationDateInstallationType: z.nativeEnum(InstallationType).optional(),
  prefferedInstallationDateManualOverride: z.boolean().optional(),
  category: z.string().optional(),
  isSelectedSkyHome: z.boolean().optional(),
  hardwareDeliveryDialog: z.string().optional(),
  hardwareDeliveryAddress: Address.optional(),
  broadbandAreaCode: z.string().optional(),
  broadbandPhoneNumber: z.string().optional(),
  showChangingProvider: z.boolean().optional(),
  changeProviderCheck: z.boolean().optional(),
  isCheckoutStarted: z.boolean().optional(),
  additionalNotes: z.string().optional(),
  deliveryInstructions: z.string().optional(),
  deliveryAddress: Address.optional(),
  alternateName: z.string().optional(),
  alternatePhoneAreaCode: z.string().optional(),
  alternatePhoneLineNumber: z.string().optional(),
  dogsAtHome: z.string().nonempty('Please select an option'),
  whoIsAtHome: z.string().default('you'),
  // HomePhoneSchema,
});

type AccountFormData = z.infer<typeof BaseAccountFormObject>;

const addIssue = (ctx: z.RefinementCtx, path: string, message: string) => {
  ctx.addIssue({ path: [path], code: z.ZodIssueCode.custom, message });
};

const isBlank = (value?: string | null) => !value || value.trim() === '';

const isInvalidDate = (value: unknown) =>
  value === null || !(value instanceof Date) || isNaN(value.getTime());

const validateContactPhone = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { category, areaCode, phoneNumber } = data;
  const parsedPhoneNumber = splitPhoneNumber(phoneNumber);
  // Broadband and DTH + Broadband journeys only accept mobile numbers.
  const phoneValidationMessage =
    category && category !== 'tv'
      ? validateBroadbandMobileNumber(phoneNumber)
      : validatePhoneNumber(phoneNumber);

  if (phoneValidationMessage !== true) {
    addIssue(ctx, 'phoneNumber', phoneValidationMessage);
  }

  if (!areaCode || areaCode.trim() === '') {
    addIssue(ctx, 'areaCode', 'Area code is required');
  } else if (parsedPhoneNumber.areaCode && areaCode.trim() !== parsedPhoneNumber.areaCode) {
    addIssue(ctx, 'areaCode', 'Area code is invalid');
  }
};

const validatePersonalDetails = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { dateOfBirth, hardwareDeliveryDialog, hardwareDeliveryAddress } = data;

  if (isInvalidDate(dateOfBirth)) {
    addIssue(ctx, 'dateOfBirth', 'Date of birth is required');
  }

  if (
    hardwareDeliveryDialog === 'Yes' &&
    (!hardwareDeliveryAddress ||
      (hardwareDeliveryAddress.label === '' && hardwareDeliveryAddress.id === '') ||
      !checkValidAddress(hardwareDeliveryAddress))
  ) {
    addIssue(ctx, 'hardwareDeliveryAddress', 'Valid hardware delivery address is required');
  }
};

const validateExistingHomePhoneAreaCode = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { phoneNumberAreaCode } = data;

  if (!phoneNumberAreaCode || phoneNumberAreaCode.trim() === '') {
    addIssue(ctx, 'phoneNumberAreaCode', 'Area code is required');
  } else if (!['03', '04', '06', '07', '09'].includes(phoneNumberAreaCode.trim())) {
    addIssue(ctx, 'phoneNumberAreaCode', 'Please enter a valid home phone area code');
  }
};

const validateExistingHomePhoneProvider = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { currentProvider, isPhoneProviderMatchBroadband } = data;

  if (!currentProvider && isPhoneProviderMatchBroadband === 'No') {
    addIssue(ctx, 'currentProvider', 'Current Provider is required');
  } else if (isNotShownProvider(currentProvider)) {
    addIssue(ctx, 'otherProvider', 'Current Provider is required');
  }
};

const validateExistingHomePhoneAccountNumber = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { accountNumberForHomePhone } = data;

  if (!accountNumberForHomePhone || accountNumberForHomePhone.trim() === '') {
    addIssue(ctx, 'accountNumberForHomePhone', 'Account number is required');
  } else if (accountNumberForHomePhone.trim().length > 9) {
    addIssue(ctx, 'accountNumberForHomePhone', 'Account number must be less than 9 digits');
  } else if (!/^\d+$/.test(accountNumberForHomePhone)) {
    addIssue(ctx, 'accountNumberForHomePhone', 'Account number must be digits ');
  }
};

const validateExistingHomePhoneLineNumber = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { phoneNumberLineNumber } = data;

  if (!phoneNumberLineNumber || phoneNumberLineNumber.trim() === '') {
    addIssue(ctx, 'phoneNumberLineNumber', 'Phone number is required');
  } else if (!/^\d+$/.test(phoneNumberLineNumber.trim())) {
    addIssue(ctx, 'phoneNumberLineNumber', 'Phone number must only contain digits');
  } else if (phoneNumberLineNumber.trim().length !== 7) {
    addIssue(ctx, 'phoneNumberLineNumber', 'Phone number must be exactly 7 digits');
  }
};

const validateHomePhone = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { isKeepExistingPhoneNumber, category, isSelectedSkyHome, phoneNumberFromList } = data;

  if (category === 'tv' || isSelectedSkyHome !== true) {
    return;
  }

  if (isKeepExistingPhoneNumber === 'Yes') {
    validateExistingHomePhoneAreaCode(data, ctx);
    validateExistingHomePhoneProvider(data, ctx);
    validateExistingHomePhoneAccountNumber(data, ctx);
    validateExistingHomePhoneLineNumber(data, ctx);
  }

  if (isKeepExistingPhoneNumber === 'No' && isBlank(phoneNumberFromList)) {
    addIssue(ctx, 'phoneNumberFromList', 'Please select a new home phone number');
  }
};

const validateBroadbandDetails = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { category, prefferedInstallationDate, broadbandAreaCode, broadbandPhoneNumber } = data;

  if (category === 'tv') {
    return;
  }

  if (
    isInvalidDate(prefferedInstallationDate) ||
    isDateBeforeToday(prefferedInstallationDate as Date)
  ) {
    addIssue(ctx, 'prefferedInstallationDate', 'Preffered Installation date is required');
  }

  if (isBlank(broadbandAreaCode)) {
    addIssue(ctx, 'broadbandAreaCode', 'Area code is required');
  }

  if (isBlank(broadbandPhoneNumber)) {
    addIssue(ctx, 'broadbandPhoneNumber', 'Phone number is required');
  }
};

const validateAlternateContact = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const { whoIsAtHome, alternateName, alternatePhoneAreaCode, alternatePhoneLineNumber } = data;

  if (whoIsAtHome !== 'someone-else') {
    return;
  }

  if (!alternateName || alternateName.trim() === '') {
    addIssue(ctx, 'alternateName', 'Alternative contact name is required');
  } else if (alternateName.trim().length < 3) {
    addIssue(ctx, 'alternateName', 'Name must be at least 3 characters');
  }

  const alternatePhoneDisplayValue =
    `${alternatePhoneAreaCode ?? ''}${alternatePhoneLineNumber ?? ''}`.trim();
  const alternatePhoneValidationMessage = validatePhoneNumber(alternatePhoneDisplayValue);

  if (isBlank(alternatePhoneAreaCode)) {
    addIssue(ctx, 'alternatePhoneAreaCode', 'Area code is required');
  }

  if (isBlank(alternatePhoneLineNumber)) {
    addIssue(ctx, 'alternatePhoneLineNumber', 'Phone number is required');
  } else if (alternatePhoneValidationMessage !== true) {
    addIssue(ctx, 'alternatePhoneLineNumber', alternatePhoneValidationMessage);
  }
};

const validateChangingProvider = (data: AccountFormData, ctx: z.RefinementCtx) => {
  const {
    showChangingProvider,
    currentServiceProvider,
    providerAccountNumber,
    changeProviderCheck,
  } = data;

  if (!showChangingProvider) {
    return;
  }

  if (!currentServiceProvider) {
    addIssue(ctx, 'currentServiceProvider', 'Select Current Service provider');
  }

  if (!providerAccountNumber) {
    addIssue(ctx, 'providerAccountNumber', 'Account number is required ');
  } else if (!/^\d+$/.test(providerAccountNumber)) {
    addIssue(ctx, 'providerAccountNumber', 'Account number must be digits ');
  } else if (providerAccountNumber.trim().length > 9) {
    addIssue(ctx, 'providerAccountNumber', 'Account number must be less than 9 digits');
  }

  if (!changeProviderCheck) {
    addIssue(ctx, 'changeProviderCheck', 'Please check the box to proceed');
  }
};

const BaseAccountFormSchema = BaseAccountFormObject.superRefine((data, ctx) => {
  validateContactPhone(data, ctx);
  validatePersonalDetails(data, ctx);
  validateHomePhone(data, ctx);
  validateBroadbandDetails(data, ctx);
  validateAlternateContact(data, ctx);
  validateChangingProvider(data, ctx);
});

export const AccountFormSchema = (
  deviceType?: string,
  category?: string,
  isSelectedSkyHome?: boolean
) => {
  // if (category === 'tv') {
  //   return BaseAccountFormSchema.omit({
  //     // accountNumberForHomePhone: true,
  //     // phoneNumberAreaCode: true,
  //     // phoneNumberFromList: true,
  //     // phoneNumberLineNumber: true,
  //     prefferedInstallationDate: true,
  //   });
  // } else if (category === 'broadband' && isSelectedSkyHome === false) {
  //   return BaseAccountFormSchema.omit({
  //     // accountNumberForHomePhone: true,
  //     //  phoneNumberAreaCode: true,
  //     // phoneNumberFromList: true,
  //     // phoneNumberLineNumber: true,
  //   });
  // }
  return BaseAccountFormSchema;
};

export const PaymentSchema = z.object({
  paymentMethod: z.string().min(1, 'Select payment option'),
  bankName: z.string().min(1, 'Bank name required').optional(),
  accountNumber: z
    .string()
    .nonempty('Account number is required')
    .min(19, 'Minimum length 16 digits')
    .refine(value => checkDigitOnly(value), 'Must be numbers')
    .refine(value => bankValidator.validate(value), 'Account number is not valid')
    .optional(),
  consent: z.boolean().optional(),
  read: z.boolean().refine(val => val === true, {
    message: 'Please check this box if you want to proceed',
  }),
  accept: z.boolean().refine(val => val === true, {
    message: 'Please check this box if you want to proceed',
  }),
  accountCheck: z.boolean().refine(val => val === true, {
    message: 'Please check this box if you want to proceed',
  }),
});

export const planSelectionSchema = z.object({});

export const AcquisitionFormSchema = {
  [CHECKOUT_TABS.plan]: planSelectionSchema,
  [CHECKOUT_TABS.aboutYou]: AccountFormSchema,
  [CHECKOUT_TABS.checkout]: PaymentSchema,
  [CHECKOUT_TABS.confirmation]: planSelectionSchema,
};

export const createAcquisitionFormSchema = (
  deviceType?: string,
  category?: string,
  isSelectedSkyHome?: boolean
) => AccountFormSchema(deviceType, category, isSelectedSkyHome).and(PaymentSchema);
export type T_AcquisitionForm = z.infer<ReturnType<typeof createAcquisitionFormSchema>>;
